import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDateTimeShort } from "@/lib/dates";
import { Loader2, Copy, Check, MessageCircle, Send, RefreshCcw, XCircle, Share2 } from "lucide-react";

/**
 * Generate a time-limited public share link for a One-Page Profile and
 * surface WhatsApp + SMS deep-links so the RM can hand the link off in seconds.
 */
export default function ShareOPPModal({ plan, onClose }) {
  const [hours, setHours] = useState(24);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/care-plans/${plan.id}/one-page-profile/shares`);
      setHistory(data.items || []);
    } catch (_) { /* swallow */ }
  };

  useEffect(() => { loadHistory(); }, [plan.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy(true); setError(""); setResult(null); setCopied(false);
    try {
      const { data } = await api.post(`/care-plans/${plan.id}/one-page-profile/share-link`, {
        expires_in_hours: hours, note,
      });
      setResult(data);
      loadHistory();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!result?.share_url) return;
    try {
      await navigator.clipboard.writeText(result.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // fallback: select inside the input
      const el = document.querySelector('[data-testid="opp-share-url-input"]');
      if (el) { el.select(); document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    }
  };

  const revoke = async (shareId) => {
    if (!window.confirm("Revoke this link? It will stop working immediately.")) return;
    try {
      await api.post(`/care-plans/${plan.id}/one-page-profile/share-link/${shareId}/revoke`);
      loadHistory();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="opp-share-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-[#0D7377]" />
            <h3 className="font-heading text-lg font-bold text-slate-900">Share One-Page Profile</h3>
          </div>
          <button onClick={onClose} data-testid="opp-share-close" className="text-slate-500 hover:text-slate-800 p-1"><XCircle className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {!result && (
            <>
              <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded p-3">
                <strong>For clinical handover only.</strong> The link works without login until it expires or is revoked. Every open is recorded.
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Link expiry</label>
                <div className="grid grid-cols-4 gap-1.5" data-testid="opp-share-expiry">
                  {[
                    { v: 1,   label: "1 hour" },
                    { v: 4,   label: "4 hours" },
                    { v: 24,  label: "24 hours" },
                    { v: 168, label: "7 days" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setHours(o.v)}
                      data-testid={`opp-share-expiry-${o.v}`}
                      className={`text-xs px-3 py-1.5 rounded border ${hours === o.v ? "bg-[#0D7377] text-white border-[#0D7377]" : "bg-white border-slate-300 hover:bg-slate-50"}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Reason (optional)</label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  data-testid="opp-share-note"
                  placeholder="e.g. A&E handover at Royal Free, paramedic Jane S."
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                />
              </div>
              {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="opp-share-error">{error}</p>}
              <button
                onClick={create}
                disabled={busy}
                data-testid="opp-share-generate"
                className="w-full px-4 py-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white rounded-md font-semibold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Generate share link
              </button>
            </>
          )}

          {result && (
            <div className="space-y-3" data-testid="opp-share-result">
              <div className="rounded bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
                Link active for <strong>{result.human_expiry}</strong>. Expires {fmtDateTimeShort(result.expires_at)}.
              </div>
              <label className="text-xs uppercase tracking-wider text-slate-500 block">Share URL</label>
              <div className="flex gap-1">
                <input
                  readOnly
                  value={result.share_url}
                  data-testid="opp-share-url-input"
                  onFocus={(e) => e.target.select()}
                  className="flex-1 px-2 py-2 border border-slate-300 rounded text-xs font-mono bg-slate-50"
                />
                <button
                  onClick={copyLink}
                  data-testid="opp-share-copy"
                  className="px-3 py-2 border border-slate-300 rounded text-xs inline-flex items-center gap-1 hover:bg-slate-50"
                >
                  {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <a
                  href={result.whatsapp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="opp-share-whatsapp"
                  className="px-3 py-2 bg-[#25D366] hover:bg-[#1eb955] text-white rounded text-sm inline-flex items-center justify-center gap-1.5"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
                <a
                  href={result.sms_url}
                  data-testid="opp-share-sms"
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded text-sm inline-flex items-center justify-center gap-1.5"
                >
                  <Send className="h-4 w-4" /> SMS
                </a>
              </div>
              <button
                onClick={() => { setResult(null); setCopied(false); }}
                data-testid="opp-share-new"
                className="text-xs text-[#0D7377] inline-flex items-center gap-1 hover:underline"
              >
                <RefreshCcw className="h-3 w-3" /> Generate another link
              </button>
            </div>
          )}

          {history.length > 0 && (
            <div className="pt-3 border-t border-slate-200" data-testid="opp-share-history">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Recent share links</p>
              <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                {history.map((h) => (
                  <li key={h.id} className="text-xs flex items-center justify-between gap-2 border-b border-slate-100 pb-1.5">
                    <div className="min-w-0">
                      <p className="text-slate-800">
                        <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${h.is_active ? "bg-emerald-500" : "bg-slate-300"}`} />
                        {h.is_active ? "Active" : h.revoked_at ? "Revoked" : "Expired"} · {h.view_count} view{h.view_count === 1 ? "" : "s"}
                      </p>
                      <p className="text-[10px] text-slate-500 truncate">
                        {fmtDateTimeShort(h.shared_at)} by {h.shared_by_name}
                        {h.note ? ` · ${h.note}` : ""}
                      </p>
                    </div>
                    {h.is_active && (
                      <button
                        onClick={() => revoke(h.id)}
                        data-testid={`opp-share-revoke-${h.id}`}
                        className="text-[11px] text-red-600 hover:underline"
                      >
                        Revoke
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
